import { type ReactNode, useCallback, useRef } from "react";
import { gsap } from "../../animations/gsapSetup.ts";
import { useReducedMotion } from "../../hooks/useReducedMotion.ts";
import styles from "./CTAButton.module.css";

interface CTAButtonProps {
  children: ReactNode;
  href?: string;
  variant?: "classic" | "ai" | "outline";
  onClick?: () => void;
  className?: string;
}

const MAGNETIC_STRENGTH = 0.3; // 0 = no effect, 1 = full tracking

export function CTAButton({
  children,
  href,
  variant = "classic",
  onClick,
  className,
}: CTAButtonProps) {
  const ref = useRef<HTMLAnchorElement & HTMLButtonElement>(null);
  const reducedMotion = useReducedMotion();

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (reducedMotion) return;
      const el = ref.current;
      if (!el) return;

      const rect = el.getBoundingClientRect();
      const x = e.clientX - rect.left - rect.width / 2;
      const y = e.clientY - rect.top - rect.height / 2;

      gsap.to(el, {
        x: x * MAGNETIC_STRENGTH,
        y: y * MAGNETIC_STRENGTH,
        duration: 0.3,
        ease: "power2.out",
      });
    },
    [reducedMotion],
  );

  const handleMouseLeave = useCallback(() => {
    if (reducedMotion) return;
    const el = ref.current;
    if (!el) return;

    gsap.to(el, {
      x: 0,
      y: 0,
      duration: 0.5,
      ease: "elastic.out(1, 0.4)",
    });
  }, [reducedMotion]);

  const cls = `${styles.button} ${styles[variant]} ${className ?? ""}`;
  const magneticProps = {
    ref,
    onMouseMove: handleMouseMove,
    onMouseLeave: handleMouseLeave,
    "data-cursor-hover": true,
  };

  if (href) {
    return (
      <a href={href} className={cls} target="_blank" rel="noopener noreferrer" {...magneticProps}>
        <span className={styles.label}>{children}</span>
      </a>
    );
  }

  return (
    <button type="button" className={cls} onClick={onClick} {...magneticProps}>
      <span className={styles.label}>{children}</span>
    </button>
  );
}
